import React, { useState } from 'react';
import { Modal } from '@stormwater-software/lib-ui';

export type AgreementAction = 'clear' | 'waive' | 'unwaive';

export interface IAgreementActionConfirmationModalProps {
  action: AgreementAction;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  /**
   * Clear only — true once both blocks are signed, so there is an executed PDF to
   * supersede. Ignored for the other actions.
   */
  isExecuted?: boolean;
  submitting?: boolean;
}

// SW2-2336 — all three of these reverse a compliance decision on the project, and all
// three record an audit event, so they share one dialog rather than three near-identical
// ones. The copy states the consequence in each case; "are you sure" on its own does not
// tell the user that inspections are about to lock or unlock.
const COPY: Record<
  AgreementAction,
  { title: string; submit: string; placeholder: string; body: (executed: boolean) => string }
> = {
  clear: {
    title: 'Clear Authorization & Agreement signatures',
    submit: 'Clear signatures',
    placeholder: 'e.g. billing address was wrong',
    body: (executed) =>
      executed
        ? 'The signatures and the executed PDF will be removed so the agreement can be corrected and signed again. Billing details are kept for editing. Inspections stay locked for this project until both parties have signed again.'
        : 'The existing signature will be removed so the agreement can be corrected and signed again. Billing details are kept for editing.',
  },
  waive: {
    title: 'Mark Authorization & Agreement as not required',
    submit: 'Not required',
    placeholder: 'e.g. covered by a master services agreement',
    body: () =>
      'This project will be exempt from the Authorization & Agreement requirement, and its inspections will no longer be held for a signature. You can restore the requirement later.',
  },
  unwaive: {
    title: 'Require the Authorization & Agreement again',
    submit: 'Require again',
    placeholder: 'e.g. waived by mistake',
    body: () =>
      'This project will need a signed Authorization & Agreement again. Inspections will be held until both parties have signed.',
  },
};

const AgreementActionConfirmationModal: React.FC<
  IAgreementActionConfirmationModalProps
> = ({ action, onClose, onConfirm, isExecuted = false, submitting }) => {
  const [reason, setReason] = useState('');
  const copy = COPY[action];
  const fieldId = `agreement-${action}-reason`;

  return (
    <Modal
      title={copy.title}
      subTitle={copy.body(isExecuted)}
      show={true}
      submitBtnText={submitting ? 'Saving…' : copy.submit}
      handleSubmit={() => onConfirm(reason.trim())}
      handleClose={onClose}
      cancelBtn={true}
    >
      <div className='form-group'>
        <label htmlFor={fieldId}>Reason (optional)</label>
        <textarea
          id={fieldId}
          className='form-control'
          rows={3}
          value={reason}
          maxLength={1000}
          disabled={submitting}
          placeholder={copy.placeholder}
          onChange={(e) => setReason(e.target.value)}
        />
        <small className='text-muted'>
          Recorded with this change for audit purposes.
        </small>
      </div>
    </Modal>
  );
};

export default AgreementActionConfirmationModal;
